import { FC, useCallback } from 'react'

import styled from 'styled-components'

import { ScheduleSettingsPageSkeleton } from 'src/Pages/Artist/ScheduleSettingsPageSkeleton'
import { ScheduleSettingsHeader } from 'src/Components/ScheduleSettingsHeader'
import {
  CampaignPlatformTypeEnum,
  SpotifyCampaignMatchingType,
} from 'src/Types/campaigns'
import { ScheduleSteps } from 'src/Types/ui'
import _dayjs from 'src/Helpers/dayjs'
import {
  getDefaultStartDate,
  getIsStartDateOutdated,
} from 'src/Constants/functions'
import { useHasPlan } from 'src/Hooks/useHasPlan'
import { CampaignTypeSelector } from 'src/Components/CampaignTypeSelector'
import { useUpdateSpotifyCampaignMatchingTypeMutation } from 'src/Redux/spotify-campaignApi'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`

export const SpotifyScheduleTypePage: FC = () => {
  const { campaignDetails, isLoading, setMatchingType } =
    useSpotifyCampaignStatus()
  const { setSearch } = useCustomSearchParams()

  const hasPlan = useHasPlan()

  const [updateMatchingType] = useUpdateSpotifyCampaignMatchingTypeMutation()

  const handleSelectType = useCallback(
    (matchingType: SpotifyCampaignMatchingType) => {
      if (!campaignDetails) {
        return
      }

      updateMatchingType({
        id: String(campaignDetails.id),
        matchingType,
      })

      setMatchingType(matchingType)
      setSearch({ step: ScheduleSteps.SETTINGS })
    },
    [campaignDetails, updateMatchingType, setMatchingType, setSearch],
  )

  if (isLoading || !campaignDetails) {
    return <ScheduleSettingsPageSkeleton />
  }

  const hasUsableStartDate =
    campaignDetails.start_date &&
    !getIsStartDateOutdated(campaignDetails.start_date, hasPlan)

  const startDate = hasUsableStartDate
    ? _dayjs(campaignDetails.start_date)
    : _dayjs(getDefaultStartDate(hasPlan))

  return (
    <Container>
      <ScheduleSettingsHeader
        artistName={campaignDetails.track.author}
        trackCover={campaignDetails.track.cover}
        trackName={campaignDetails.track.title}
        platform={CampaignPlatformTypeEnum.SPOTIFY}
        startDate={startDate}
        todayEnable={hasPlan}
        isEditable={false}
      />

      <CampaignTypeSelector onSelectType={handleSelectType} />
    </Container>
  )
}
